/**
 * What "Get Reading" means, given what the Mystic is offering.
 *
 * The button carries the reader's name and the price of a reading, and used to
 * open a drawer showing a menu — "Pull Tarot Cards" / "Education" — so a seeker
 * who had already said what they wanted, and seen what it cost, was asked
 * again. The price is the tell: it can only be quoted because we already know
 * which reading is meant.
 *
 * A button goes straight to the thing it names. The one honest exception is
 * ambiguity: a Mystic offering more than one KIND of reading cannot have a
 * single "Get Reading", and there the menu is the truthful answer rather than
 * a detour.
 *
 * Pure: it decides, the caller performs.
 */

const SKILL_PREFIX = 'skill:';

/** Actions that ARE a reading, as opposed to something else on the menu. */
function readingActions(actions) {
  return (Array.isArray(actions) ? actions : []).filter(
    (a) => typeof (a && a.action_type) === 'string'
      && a.action_type.startsWith(SKILL_PREFIX),
  );
}

export function readingIntent(actions) {
  const list = Array.isArray(actions) ? actions : [];
  const type = (a) => (a && a.action_type) || '';

  // An unfinished reading outranks starting a new one. The seeker has already
  // paid for that sitting; sending them to a fresh draw would charge them
  // twice for it. Start Over rides alongside Continue and is not a second
  // reading on offer, so it never makes this ambiguous.
  // The ACTION itself comes back beside the trigger: the caller must perform
  // it the way the menu tile does (routeQuickAction → the conversation
  // round-trip), not merely "trigger" it. Naming the trigger without the
  // action left Get Reading opening the drawer and then… showing the menu.
  const cont = list.find((a) => type(a) === 'continue_reading');
  if (cont) {
    return { trigger: 'continue_reading', openMenu: false, action: cont };
  }

  const readings = readingActions(list);
  if (readings.length === 1) {
    return { trigger: type(readings[0]), openMenu: false, action: readings[0] };
  }

  // Nothing loaded yet, nothing on offer, or genuinely more than one kind —
  // the menu is the honest answer.
  return { trigger: null, openMenu: true, action: null };
}

/**
 * The gift on the menu's "Continue the reading for <name>" (continue_gift): a
 * reading for someone left unfinished is continued from the menu by name,
 * never by Get Reading, which is always the seeker's own (2026-11-01, Chris's
 * reading for Chi came out as his own). The app holds it as store.reading_gift
 * so the reading keeps its "for" framing and its end sends the package.
 */
/** The menu's "Continue the reading for <name>" action, or null. Shown in the
 * opened ☰ Menu only while one exists (decision 2026-11-01). */
export function pausedGift(actions) {
  const list = Array.isArray(actions) ? actions : []
  return list.find((a) => (a && a.action_type) === 'continue_gift') || null
}

export function continueGift(actions) {
  const list = Array.isArray(actions) ? actions : []
  const cont = list.find((a) => (a && a.action_type) === 'continue_gift')
  const gift = cont && cont.gift
  return gift && gift.is_gift && (gift.for_name || '').trim() ? gift : null
}

/**
 * What "A reading for someone" means: always a tarot reading (a care package
 * is a reading made for them). A horoscope on the same menu does not make it
 * ambiguous, and an unfinished reading of the seeker's own is not what it
 * continues. `waiting` while the menu has not loaded, so the caller can act
 * once it does.
 */
export function readingForSomeoneIntent(actions) {
  const list = Array.isArray(actions) ? actions : [];
  if (!list.length) return { action: null, waiting: true };
  const tarot = list.find((a) => (a && a.action_type) === `${SKILL_PREFIX}tarot`);
  return { action: tarot || null, waiting: false };
}
